import {
  useReviewTasksMutation,
  useUndoTaskReviewsMutation,
} from "@common/api/quoting/quote"
import type { QuoteTaskReview, QuoteTaskReviewUndo } from "@common/client"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { handleError } from "@common/utils"
import useCustomToast from "@/hooks/use-custom-toast"

export function useReviewTasks(quoteId: string): {
  review: (body: QuoteTaskReview, onDone?: () => void) => void
  pending: boolean
} {
  const mutation = useReviewTasksMutation()
  const showToast = useCustomToast()
  const trackEvent = useTrackEvent()

  const review = (body: QuoteTaskReview, onDone?: () => void) =>
    mutation.mutate(
      { quote_id: quoteId, quoteTaskReview: body },
      {
        onSuccess: () => {
          for (const taskId of body.task_ids) {
            trackEvent("quote_task_review_decided", {
              quote_id: quoteId,
              quote_task_id: taskId,
              decision: body.decision,
              has_comment: Boolean(body.comment),
              is_bulk: body.task_ids.length > 1,
            })
          }
          onDone?.()
        },
        onError: (err) => handleError(err, showToast),
      },
    )

  return { review, pending: mutation.isPending }
}

export function useUndoReviews(quoteId: string): {
  undo: (body: QuoteTaskReviewUndo) => void
  pending: boolean
} {
  const mutation = useUndoTaskReviewsMutation()
  const showToast = useCustomToast()

  const undo = (body: QuoteTaskReviewUndo) =>
    mutation.mutate(
      { quote_id: quoteId, quoteTaskReviewUndo: body },
      { onError: (err) => handleError(err, showToast) },
    )

  return { undo, pending: mutation.isPending }
}
